import * as React from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, IconButton, LinearProgress, Link, Stack, Table, TableBody, TableCell, TableHead,
    TableRow, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink } from 'react-router-dom';
import { getMatchBrief } from '../../Api';
import { MatchBriefDto, MatchBriefStatus } from '../../models/dtos/MatchBriefDto';

const STATUS_COLORS: Record<MatchBriefStatus, 'success' | 'info' | 'warning' | 'error'> = {
    READY: 'success',
    RESEARCHING: 'info',
    PENDING: 'warning',
    FAILED: 'error',
};

export const matchBriefStatusChip = (status: MatchBriefStatus) =>
    <Chip size="small" color={STATUS_COLORS[status] ?? 'default'} label={status} />;

export const matchBriefTitle = (brief: Pick<MatchBriefDto, 'match'>) =>
    brief.match.home && brief.match.away ? `${brief.match.home} – ${brief.match.away}` : `Match ${brief.match.id}`;

const pct = (value?: number | null) => (value == null ? '—' : `${Math.round(value * 100)}%`);

const formatDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

const PART_LABELS: Record<string, string> = { match: 'Match', '1st_half': '1st half', '2nd_half': '2nd half' };

function ProbabilityBar({ label, value, highlight }: { label: string; value?: number; highlight: boolean }) {
    return (
        <Stack direction="row" alignItems="center" gap={1.5}>
            <Typography variant="body2" sx={{ width: 48, fontWeight: highlight ? 700 : 400 }}>{label}</Typography>
            <LinearProgress variant="determinate" value={(value ?? 0) * 100} color={highlight ? 'primary' : 'inherit'}
                sx={{ flex: 1, height: 10, borderRadius: 5 }} />
            <Typography variant="body2" sx={{ width: 44, textAlign: 'right', fontWeight: highlight ? 700 : 400 }}>{pct(value)}</Typography>
        </Stack>
    );
}

function Source({ source }: { source?: string }) {
    if (!source) return null;
    return /^https?:\/\//i.test(source)
        ? <Link href={source} target="_blank" rel="noopener noreferrer" variant="caption" sx={{ wordBreak: 'break-all' }}>{source}</Link>
        : <Typography variant="caption" color="text.secondary">{source}</Typography>;
}

/**
 * One AI match brief, read-only: the analysis in English or one of its translations, the numbers, the factors with
 * their sources, and how it was made (status, attempts, research run, findings). [navigable] adds previous / next
 * (the briefs list's order, newest first).
 */
export default function MatchBriefDialog({ briefId, onClose, navigable = false, showMatchLink = true }: {
    briefId: number | null;
    onClose: () => void;
    navigable?: boolean;
    showMatchLink?: boolean;
}) {
    const [currentId, setCurrentId] = React.useState<number | null>(briefId);
    const [brief, setBrief] = React.useState<MatchBriefDto | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [failed, setFailed] = React.useState(false);
    const [language, setLanguage] = React.useState('en');

    React.useEffect(() => setCurrentId(briefId), [briefId]);

    React.useEffect(() => {
        if (currentId == null) return;
        let cancelled = false;
        setLoading(true);
        setFailed(false);
        getMatchBrief(currentId).then(result => {
            if (cancelled) return;
            setBrief(result);
            setFailed(result == null);
            setLoading(false);
            setLanguage(lang => (lang === 'en' || result?.languages.includes(lang) ? lang : 'en'));
        });
        return () => { cancelled = true; };
    }, [currentId]);

    const english = brief?.brief ?? null;
    const translated = language !== 'en' ? brief?.translations?.[language] : undefined;
    const text = (field: 'headline' | 'summary' | 'market_view') => translated?.[field] || english?.[field] || '';
    const factors = (english?.factors ?? []).map((f, i) => ({ ...f, text: translated?.factors?.[i] || f.text }));
    const probabilities = english?.probabilities;

    return (
        <Dialog open={briefId != null} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
            <DialogTitle sx={{ pr: navigable ? 12 : 3 }}>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <span>{brief ? matchBriefTitle(brief) : 'AI prediction'}</span>
                    {brief && matchBriefStatusChip(brief.status)}
                    {brief && brief.app_status !== 'ready' && <Chip size="small" variant="outlined" label={`app: ${brief.app_status.replace('_', ' ')}`} />}
                </Stack>
                {brief && (
                    <Typography variant="body2" color="text.secondary">
                        {[brief.match.league, `kick-off ${formatDate(brief.match.start)}`].filter(Boolean).join(' · ')}
                        {showMatchLink && <> · <Link component={RouterLink} to={`/matches/${brief.match.id}`} onClick={onClose}>open match {brief.match.id}</Link></>}
                    </Typography>
                )}
                {navigable && (
                    <Box sx={{ position: 'absolute', right: 12, top: 12 }}>
                        <Tooltip title="Newer brief"><span>
                            <IconButton disabled={!brief?.previous_id || loading} onClick={() => setCurrentId(brief!.previous_id!)}><ChevronLeftIcon /></IconButton>
                        </span></Tooltip>
                        <Tooltip title="Older brief"><span>
                            <IconButton disabled={!brief?.next_id || loading} onClick={() => setCurrentId(brief!.next_id!)}><ChevronRightIcon /></IconButton>
                        </span></Tooltip>
                    </Box>
                )}
            </DialogTitle>
            <DialogContent dividers>
                {loading && !brief && <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>}
                {failed && <Alert severity="error">The brief could not be loaded.</Alert>}
                {brief && (
                    <Stack gap={2.5} sx={{ opacity: loading ? 0.5 : 1 }}>
                        {brief.error && <Alert severity={brief.status === 'FAILED' ? 'error' : 'warning'}>{brief.error}</Alert>}
                        {english ? (
                            <>
                                {brief.languages.length > 0 && (
                                    <ToggleButtonGroup size="small" exclusive value={language} onChange={(_, v) => v && setLanguage(v)}>
                                        {['en', ...brief.languages].map(l => <ToggleButton key={l} value={l}>{l.toUpperCase()}</ToggleButton>)}
                                    </ToggleButtonGroup>
                                )}
                                <Box>
                                    <Typography variant="h6" gutterBottom>{text('headline')}</Typography>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{text('summary')}</Typography>
                                </Box>
                                <Stack direction="row" gap={1} flexWrap="wrap">
                                    {english.pick && <Chip color="primary" label={`Pick: ${english.pick}`} />}
                                    {english.confidence && <Chip variant="outlined" label={`Confidence: ${english.confidence}`} />}
                                    <Chip variant="outlined" label={english.research_used === false ? 'BETUP data only' : 'Web research used'} />
                                </Stack>
                                {probabilities && (
                                    <Stack gap={1}>
                                        <Typography variant="subtitle2">Probabilities</Typography>
                                        <ProbabilityBar label={brief.match.home ?? 'Home'} value={probabilities.home} highlight={english.pick === 'home'} />
                                        <ProbabilityBar label="Draw" value={probabilities.draw} highlight={english.pick === 'draw'} />
                                        <ProbabilityBar label={brief.match.away ?? 'Away'} value={probabilities.away} highlight={english.pick === 'away'} />
                                    </Stack>
                                )}
                                {text('market_view') && (
                                    <Box>
                                        <Typography variant="subtitle2">Market view</Typography>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{text('market_view')}</Typography>
                                    </Box>
                                )}
                                {((english.totals?.length ?? 0) > 0 || (english.halves?.length ?? 0) > 0) && (
                                    <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                                        {(english.totals?.length ?? 0) > 0 && (
                                            <Table size="small" sx={{ flex: 1 }}>
                                                <TableHead><TableRow><TableCell>Totals</TableCell><TableCell>Line</TableCell><TableCell>Over</TableCell><TableCell>Under</TableCell></TableRow></TableHead>
                                                <TableBody>{english.totals!.map((t, i) => (
                                                    <TableRow key={i}><TableCell>{PART_LABELS[t.part ?? ''] ?? t.part}</TableCell><TableCell>{t.line}</TableCell>
                                                        <TableCell>{pct(t.over)}</TableCell><TableCell>{t.over == null ? '—' : pct(1 - t.over)}</TableCell></TableRow>
                                                ))}</TableBody>
                                            </Table>
                                        )}
                                        {(english.halves?.length ?? 0) > 0 && (
                                            <Table size="small" sx={{ flex: 1 }}>
                                                <TableHead><TableRow><TableCell>Halves</TableCell><TableCell>Home</TableCell><TableCell>Draw</TableCell><TableCell>Away</TableCell></TableRow></TableHead>
                                                <TableBody>{english.halves!.map((h, i) => (
                                                    <TableRow key={i}><TableCell>{PART_LABELS[h.part ?? ''] ?? h.part}</TableCell><TableCell>{pct(h.home)}</TableCell>
                                                        <TableCell>{pct(h.draw)}</TableCell><TableCell>{pct(h.away)}</TableCell></TableRow>
                                                ))}</TableBody>
                                            </Table>
                                        )}
                                    </Stack>
                                )}
                                {factors.length > 0 && (
                                    <Stack gap={1}>
                                        <Typography variant="subtitle2">Factors</Typography>
                                        {factors.map((f, i) => (
                                            <Stack key={i} direction="row" gap={1.5} alignItems="flex-start">
                                                <Chip size="small" variant="outlined" sx={{ minWidth: 72 }}
                                                    color={f.favours === 'home' || f.favours === 'away' ? 'primary' : 'default'}
                                                    label={f.favours === 'home' ? (brief.match.home ?? 'home') : f.favours === 'away' ? (brief.match.away ?? 'away') : 'neutral'} />
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="body2">{f.text}</Typography>
                                                    <Source source={f.source} />
                                                </Box>
                                            </Stack>
                                        ))}
                                    </Stack>
                                )}
                            </>
                        ) : (
                            <Typography color="text.secondary">
                                {brief.status === 'FAILED' ? 'No brief: it failed.' : 'The brief is not written yet.'}
                            </Typography>
                        )}
                        <Divider />
                        <Stack direction="row" gap={3} flexWrap="wrap">
                            <Meta label="Bets when selected" value={brief.bets.toLocaleString()} />
                            <Meta label="Attempts" value={String(brief.attempts)} />
                            <Meta label="Queued" value={formatDate(brief.created_at)} />
                            <Meta label="Ready" value={formatDate(brief.ready_at)} />
                            <Meta label="Translations" value={brief.languages.length ? brief.languages.join(', ') : '—'} />
                            {brief.research_run_id && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">Research run</Typography>
                                    <Link component={RouterLink} to={`/ai/runs/${brief.research_run_id}`} onClick={onClose} variant="body2">{brief.research_run_id.slice(0, 8)}…</Link>
                                </Box>
                            )}
                        </Stack>
                        {brief.findings && (
                            <Accordion disableGutters variant="outlined">
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="subtitle2">
                                        Researcher findings (JSON){brief.research_id ? ` · research #${brief.research_id}` : ''}{brief.researched_at ? `, ${formatDate(brief.researched_at)}` : ''}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(brief.findings, null, 2)}</Box>
                                </AccordionDetails>
                            </Accordion>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
        </Dialog>
    );
}

function Meta({ label, value }: { label: string; value: string }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
            <Typography variant="body2">{value}</Typography>
        </Box>
    );
}
